/**
 * Latest-book hook — feeds the home indexing notice the newest book in the
 * corpus, so "la biblioteca sigue creciendo" can show evidence rather than
 * just assert it.
 *
 * Reads `/corpus-data/latest.json`, a ~100-byte artifact written by
 * scripts/corpus-reader-manifest.ts alongside the full manifest. Deliberately
 * NOT the manifest itself: that file is ~277KB and the idle home screen must
 * not pay for it to name one book.
 *
 * `cache: "no-cache"` forces revalidation (conditional GET → 304 when
 * unchanged, fresh 200 after a corpus deploy). Without it browsers apply
 * heuristic caching to these no-Cache-Control static files and a newly
 * published book stays invisible — the same trap use-corpus-data.ts documents.
 *
 * Fails soft to `null` in every failure mode (offline, 404 on an older deploy
 * that predates latest.json, malformed payload). The notice simply renders
 * without its footer, exactly as it does today.
 */
import { useEffect, useState } from "react";

export interface LatestBook {
  slug: string;
  name: string;
  chapters: number;
  /** Lowest chapter number — the deep-link target into the reader. */
  firstChapter: number;
}

interface LatestPayload {
  book?: { slug?: unknown; name?: unknown; chapters?: unknown; first_chapter?: unknown } | null;
}

export function useLatestBook(): LatestBook | null {
  const [book, setBook] = useState<LatestBook | null>(null);

  useEffect(() => {
    // The fetch may settle after unmount; suppress that stale state write.
    let alive = true;
    fetch("/corpus-data/latest.json", {
      headers: { Accept: "application/json" },
      cache: "no-cache",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: LatestPayload) => {
        if (!alive) return;
        const b = data?.book;
        if (
          b === null ||
          b === undefined ||
          typeof b.slug !== "string" ||
          typeof b.name !== "string" ||
          typeof b.chapters !== "number" ||
          b.chapters <= 0 ||
          typeof b.first_chapter !== "number"
        ) {
          return; // stays null — notice renders without the footer
        }
        setBook({ slug: b.slug, name: b.name, chapters: b.chapters, firstChapter: b.first_chapter });
      })
      .catch(() => {
        /* fail soft — the notice is complete without it */
      });
    return () => {
      alive = false;
    };
  }, []);

  return book;
}
